import { useCallback } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import { useUpdateChatEnabledDataMutationMutation } from '../../../generated';

interface UseUpdateChatEnabledConfig {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export interface UseUpdateChatEnabledResponse {
  updateChatEnabled: (variables: {
    id: string;
    enabled: boolean;
  }) => Promise<void>;
  isUpdating: boolean;
  error: Error | null;
}

export const useUpdateChatEnabled = ({
  onSuccess,
  onError,
}: UseUpdateChatEnabledConfig = {}): UseUpdateChatEnabledResponse => {
  const { addNotification } = useNotification();

  const [updateChatEnabledMutation, { loading: isUpdating, error }] =
    useUpdateChatEnabledDataMutationMutation({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: false,
      onCompleted: () => {
        onSuccess?.();
      },
      onError: (mutationError) => {
        const errorObj = new Error(mutationError.message);
        onError?.(errorObj);
      },
      // Update cache locally without refetching
      update: (cache, { data }, { variables }) => {
        if (!variables) return;

        // Update the specific chat entity in cache
        const chatId = cache.identify({ __typename: 'Chat', id: variables.id });
        if (chatId) {
          cache.modify({
            id: chatId,
            fields: {
              enabled() {
                return variables.enabled;
              },
            },
          });
        }
      },
    });

  const updateChatEnabled = useCallback(
    async (variables: { id: string; enabled: boolean }) => {
      try {
        await updateChatEnabledMutation({ variables });

        const toggleStateMessage = variables.enabled ? 'enabled' : 'disabled';
        addNotification({
          title: `CHAT ${toggleStateMessage.toUpperCase()}`,
          message: `Chat was successfully ${toggleStateMessage}`,
          type: 'success',
        });
      } catch (err) {
        const toggleStateMessage = variables.enabled ? 'enabled' : 'disabled';
        addNotification({
          title: `CHAT ${toggleStateMessage.toUpperCase()}`,
          message: `Chat cannot be ${toggleStateMessage} now 😰. Try again later.`,
          type: 'error',
        });
        throw err;
      }
    },
    [updateChatEnabledMutation, addNotification]
  );

  return {
    updateChatEnabled,
    isUpdating,
    error: error ? new Error(error.message) : null,
  };
};
